/**
 * Document Data Hooks
 *
 * Wraps Convex queries/mutations and transforms backend UIDocument
 * into DocumentView for the UI layer.
 */

import { keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "convex/_generated/api";
import { useMemo } from "react";
import { toDocumentView } from "@/packages/types";
import type { DocumentView, Id, UserId } from "@/packages/types";
import { useApiMutation } from "@/packages/features-shared";
import { useWorkspaceQuery, useWorkspaceId } from "@/hooks";

/* ------------------------------------------------------------------ */
/* useDocuments                                                        */
/* ------------------------------------------------------------------ */

interface UseDocumentsFilters {
  search?: string;
  tagIds?: string[];
  fileType?: string;
  statuses?: string[];
  statusOperator?: "is" | "is_not";
  categoryIds?: string[];
  categoryOperator?: "is" | "is_not";
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

interface UseDocumentsReturn {
  documents: DocumentView[] | undefined;
  isLoading: boolean;
}

export function useDocuments(
  filters?: UseDocumentsFilters,
): UseDocumentsReturn {
  const { data, isLoading } = useWorkspaceQuery(
    api.document_queries.listDocuments,
    {
      search: filters?.search,
      tagIds: filters?.tagIds,
      fileType: filters?.fileType,
      statuses: filters?.statuses,
      statusOperator: filters?.statusOperator,
      categoryIds: filters?.categoryIds,
      categoryOperator: filters?.categoryOperator,
      sortBy: filters?.sortBy,
      sortOrder: filters?.sortOrder,
    },
    { placeholderData: keepPreviousData },
  );

  const documents = useMemo(() => {
    if (!data) return undefined;
    return data.map((doc) => toDocumentView(doc));
  }, [data]);

  return { documents, isLoading };
}

/* ------------------------------------------------------------------ */
/* useDocumentById                                                     */
/* ------------------------------------------------------------------ */

interface UseDocumentByIdReturn {
  document: DocumentView | null | undefined;
  isLoading: boolean;
}

export function useDocumentById(
  id: Id<"documents"> | null,
): UseDocumentByIdReturn {
  const { data, isLoading } = useWorkspaceQuery(
    api.document_queries.getDocumentById,
    id ? { id } : "skip",
  );

  const document = useMemo(() => {
    if (data === undefined) return undefined;
    if (data === null) return null;
    return toDocumentView(data);
  }, [data]);

  return { document, isLoading };
}

/* ------------------------------------------------------------------ */
/* useDocumentTags                                                     */
/* ------------------------------------------------------------------ */

export interface DocumentTagOption {
  id: string;
  name: string;
  color: string;
}

interface UseDocumentTagsReturn {
  tags: DocumentTagOption[];
  isLoading: boolean;
}

export function useDocumentTags(): UseDocumentTagsReturn {
  const { data, isLoading } = useWorkspaceQuery(
    api.document_queries.getAllTags,
    {},
  );

  const tags: DocumentTagOption[] = useMemo(() => {
    if (!data) return [];
    return data.map((tag) => ({
      id: tag.id,
      name: tag.name,
      color: tag.color,
    }));
  }, [data]);

  return { tags, isLoading };
}

/* ------------------------------------------------------------------ */
/* useTagUsageCounts                                                   */
/* ------------------------------------------------------------------ */

export function useTagUsageCounts() {
  const { data, isLoading } = useWorkspaceQuery(
    api.document_queries.getTagUsageCounts,
    {},
  );

  const usageMap = useMemo(() => {
    const map = new Map<string, number>();
    if (!data) return map;
    for (const item of data) {
      map.set(item.id, item.usageCount);
    }
    return map;
  }, [data]);

  return { usageMap, isLoading };
}

/* ------------------------------------------------------------------ */
/* useDeleteDocument                                                   */
/* ------------------------------------------------------------------ */

export function useDeleteDocument() {
  const { t } = useTranslation("documents");
  const workspaceId = useWorkspaceId();
  const mutation = useApiMutation(api.document_mutations.deleteDocument, {
    name: "delete-document",
    successMessage: t("actions.successDelete"),
  });

  return (args: { id: Id<"documents">; hardDelete?: boolean }) =>
    mutation.mutateAsync({ ...args, workspaceId });
}

/* ------------------------------------------------------------------ */
/* useRenameDocument                                                   */
/* ------------------------------------------------------------------ */

export function useRenameDocument() {
  const { t } = useTranslation("documents");
  const workspaceId = useWorkspaceId();
  const mutation = useApiMutation(api.document_mutations.renameDocument, {
    name: "rename-document",
    successMessage: t("actions.successRename"),
  });

  return (args: { id: Id<"documents">; fileName: string }) =>
    mutation.mutateAsync({ ...args, workspaceId });
}

/* ------------------------------------------------------------------ */
/* useDocumentAuthor                                                    */
/* ------------------------------------------------------------------ */

interface UseDocumentAuthorReturn {
  name: string | null;
  email: string | null;
  isLoading: boolean;
}

export function useDocumentAuthor(
  userId: UserId | undefined,
): UseDocumentAuthorReturn {
  const { data, isLoading } = useWorkspaceQuery(
    api.document_queries.getDocumentAuthor,
    userId ? { userId: userId } : "skip",
  );

  return {
    name: data?.name ?? null,
    email: data?.email ?? null,
    isLoading,
  };
}

/* ------------------------------------------------------------------ */
/* useCreateDocumentTag                                                */
/* ------------------------------------------------------------------ */

export function useCreateDocumentTag() {
  const { t } = useTranslation("documents");
  const workspaceId = useWorkspaceId();
  const mutation = useApiMutation(
    api.document_tag_mutations.createDocumentTag,
    {
      name: "create-document-tag",
      successMessage: t("tags.successCreate"),
    },
  );

  return (args: { name: string; color: string }) =>
    mutation.mutateAsync({ ...args, workspaceId });
}

/* ------------------------------------------------------------------ */
/* useUpdateDocumentTag                                                */
/* ------------------------------------------------------------------ */

export function useUpdateDocumentTag() {
  const { t } = useTranslation("documents");
  const workspaceId = useWorkspaceId();
  const mutation = useApiMutation(
    api.document_tag_mutations.updateDocumentTag,
    {
      name: "update-document-tag",
      successMessage: t("tags.successUpdate"),
    },
  );

  return (args: { id: Id<"documentTags">; name?: string; color?: string }) =>
    mutation.mutateAsync({ ...args, workspaceId });
}

/* ------------------------------------------------------------------ */
/* useDeleteDocumentTag                                                */
/* ------------------------------------------------------------------ */

export function useDeleteDocumentTag() {
  const { t } = useTranslation("documents");
  const workspaceId = useWorkspaceId();
  const mutation = useApiMutation(
    api.document_tag_mutations.deleteDocumentTag,
    {
      name: "delete-document-tag",
      successMessage: t("tags.successDelete"),
    },
  );

  return (args: { id: Id<"documentTags"> }) =>
    mutation.mutateAsync({ ...args, workspaceId });
}

/* ------------------------------------------------------------------ */
/* useUpdateDocumentTags                                              */
/* ------------------------------------------------------------------ */

export function useUpdateDocumentTags() {
  const { t } = useTranslation("documents");
  const workspaceId = useWorkspaceId();
  const mutation = useApiMutation(
    api.document_tag_mutations.updateDocumentTags,
    {
      name: "update-document-tags",
      successMessage: t("tags.successAssign"),
    },
  );

  return (args: {
    documentId: Id<"documents">;
    tagIds: Id<"documentTags">[];
  }) => mutation.mutateAsync({ ...args, workspaceId });
}

/* ------------------------------------------------------------------ */
/* useRegenerateSummary                                                */
/* ------------------------------------------------------------------ */

export function useRegenerateSummary() {
  const mutation = useApiMutation(api.document_mutations.regenerateSummary, {
    name: "regenerate-summary",
  });

  return mutation;
}

/* ------------------------------------------------------------------ */
/* useUpdateDocument                                                   */
/* ------------------------------------------------------------------ */

export function useUpdateDocument() {
  const { t } = useTranslation("documents");
  const workspaceId = useWorkspaceId();
  const mutation = useApiMutation(api.document_mutations.update, {
    name: "update-document",
    successMessage: t("actions.successUpdate"),
  });

  return (args: {
    id: Id<"documents">;
    categoryId?: Id<"documentCategories">;
    expirationDate?: number;
  }) => mutation.mutateAsync({ ...args, workspaceId });
}
